import React, { useEffect, useRef, useState } from 'react';
import { CssTextAlign } from 'gf-types';
import { FieldTypes } from '../../../../helpers/constants';
import { getRgbaColorString } from '../../../../helpers/helpers';
import { ValueChangedForFieldFunc } from '../../../formeditor-types';
import { SendAFormInteractive, withSendAFormInteractiveState } from '../../SendAFormInteractiveState';
import { useAppSelector } from '../../../../hooks/ReduxHooks';
import { canIUse, Features } from '../../../../helpers/PermissionUtility';
import { getAllInvalidFields } from '../../FieldValidations/selectors';
import { useActions } from '../../../../hooks/useActions';
import { resetHighlight } from '../../../actions';
import FieldValidationPopupWrapper from '../FieldValidations/FieldValidationPopupWrapper';

export type InputFieldProps = {
  align?: CssTextAlign;
  backgroundColor?: TypeMePlease;
  characterLimit?: number;
  editable?: boolean;
  fieldId?: string;
  fieldType?: number;
  focusColor: TypeMePlease;
  fontName?: string;
  fontSize?: number;
  foregroundColor?: TypeMePlease;
  id?: string;
  lineHeight?: number;
  padding?: number;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  tabIndex?: number;
  value?: string;
  valueUpdated: ValueChangedForFieldFunc;
} & SendAFormInteractive;

function InputField(props: InputFieldProps) {
  const {
    align,
    backgroundColor,
    characterLimit,
    editable,
    fieldId,
    fieldType,
    focusColor,
    fontName,
    fontSize,
    foregroundColor,
    highlightColor,
    id,
    interactive,
    lineHeight,
    padding,
    x,
    y,
    w,
    h,
    sendAFormSelected,
    tabIndex,
    value,
    onSendAFormFieldClick,
    valueUpdated
  } = props;

  const [focused, setFocused] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const resetHighlightAction = useActions(resetHighlight);

  const formEditorState = useAppSelector((state) => state.formeditor);
  const highlighted = formEditorState.highlight?.fieldId === fieldId;
  const missingRequiredFieldIds = formEditorState.missingRequiredFieldIds || [];
  const isInvalid = getAllInvalidFields(formEditorState).find((x) => x.fieldId === fieldId);

  const errorSettings = isInvalid?.errorSettings;
  const missingRequired = missingRequiredFieldIds.includes(fieldId);

  // We need these refs to ensure that the valueUpdated function is called with the correct value when the component unmounts
  const focusedRef = useRef(focused);
  const localValueRef = useRef(localValue);

  // Update the refs whenever focused or localValue changes
  useEffect(() => {
    focusedRef.current = focused;
  }, [focused]);

  useEffect(() => {
    localValueRef.current = localValue;
  }, [localValue]);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // This effect is used to call valueUpdated when the component unmounts
  // this replaces the componentWillUnmount lifecycle method
  useEffect(() => {
    return () => {
      if (editable && interactive && focusedRef.current) {
        valueUpdated({ value: localValueRef.current });
      }
    };
  }, []);

  const handleBlur = () => {
    resetHighlightAction();
    setFocused(false);

    if (editable && interactive) {
      valueUpdated({ value: localValue });
    }
  };

  const handleFocus = () => {
    if (!interactive) {
      return;
    }

    resetHighlightAction();
    setFocused(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.currentTarget.tagName.toLowerCase() === 'input') {
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (fieldType === FieldTypes.Number && e.target.value !== '' && !e.target.value.match(/^[0-9,!@#$%^&*()_+:;<>?/=\-.]+$/)) {
      return;
    }
    setLocalValue(e.target.value);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (interactive) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    onSendAFormFieldClick(fieldId, 'form');
  };

  const effectiveBackgroundColor = focused ? focusColor : backgroundColor;
  const border = !sendAFormSelected ? '2px dashed #000' : undefined;

  const inputStyle: React.CSSProperties = {
    width: `${w}px`,
    height: `${h}px`,
    left: `${x}px`,
    top: `${y}px`,
    textAlign: align,
    fontFamily: fontName,
    fontSize,
    color: getRgbaColorString(foregroundColor),
    paddingLeft: padding !== undefined ? `${padding}px` : '0px',
    paddingRight: padding !== undefined ? `${padding}px` : '0px',
    border: interactive ? undefined : border,
    background: editable || !interactive ? (sendAFormSelected ? highlightColor : getRgbaColorString(effectiveBackgroundColor)) : undefined,
    lineHeight: fieldType === FieldTypes.TextArea && lineHeight ? `${lineHeight}px` : undefined
  };

  return (
    <>
      {canIUse(Features.FieldValidations) && (
        <FieldValidationPopupWrapper
          className="depth-three"
          fieldId={fieldId}
          x={x}
          y={y}
          width={w}
          height={h}
          errorSettings={errorSettings}
          fieldType={fieldType}
          focused={focused}
          highlighted={highlighted}
          isMissingRequired={missingRequired}
        />
      )}
      {fieldType === FieldTypes.TextArea ? (
        <textarea
          id={id}
          data-fieldid={fieldId}
          style={inputStyle}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onClick={handleClick}
          value={localValue}
          readOnly={!editable || !interactive}
          tabIndex={editable ? tabIndex : -1}
          maxLength={characterLimit}
        />
      ) : (
        <input
          id={id}
          data-fieldid={fieldId}
          type="text"
          style={inputStyle}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onClick={handleClick}
          value={localValue}
          readOnly={!editable || !interactive}
          tabIndex={editable ? tabIndex : -1}
          autoComplete="stop-autocomplete"
          maxLength={characterLimit}
        />
      )}
    </>
  );
}

export default withSendAFormInteractiveState(InputField as React.ComponentType<InputFieldProps>);
